import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import type {
    VehicleTemplate,
    CreateVehicleTemplateDto,
    UpdateVehicleTemplateDto
} from '../../types/model/vehicle-template.model';
import { vehicleTemplatesService } from '../../services/api';

// Estado inicial
export interface VehicleTemplatesState {
    templates: VehicleTemplate[];
    selectedTemplate: VehicleTemplate | null;
    isLoading: boolean;
    error: string | null;
}

const initialState: VehicleTemplatesState = {
    templates: [],
    selectedTemplate: null,
    isLoading: false,
    error: null,
};

// ==================== ASYNC THUNKS ====================

// Obtener todos los templates
export const fetchVehicleTemplates = createAsyncThunk(
    'vehicleTemplates/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const templates = await vehicleTemplatesService.getAll();
            return templates;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || 'Error al cargar templates'
            );
        }
    }
);

// Obtener un template por ID
export const fetchVehicleTemplateById = createAsyncThunk(
    'vehicleTemplates/fetchById',
    async (id: string, { rejectWithValue }) => {
        try {
            const template = await vehicleTemplatesService.getById(id);
            return template;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || 'Error al cargar template'
            );
        }
    }
);

// Crear un nuevo template
export const createVehicleTemplate = createAsyncThunk(
    'vehicleTemplates/create',
    async (templateData: CreateVehicleTemplateDto, { rejectWithValue }) => {
        try {
            const newTemplate = await vehicleTemplatesService.create(templateData);
            return newTemplate;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || 'Error al crear template'
            );
        }
    }
);

// Actualizar un template
export const updateVehicleTemplate = createAsyncThunk(
    'vehicleTemplates/update',
    async ({ id, data }: { id: string; data: UpdateVehicleTemplateDto }, { rejectWithValue }) => {
        try {
            const updatedTemplate = await vehicleTemplatesService.update(id, data);
            return updatedTemplate;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || 'Error al actualizar template'
            );
        }
    }
);

// Eliminar un template
export const deleteVehicleTemplate = createAsyncThunk(
    'vehicleTemplates/delete',
    async (id: string, { rejectWithValue }) => {
        try {
            await vehicleTemplatesService.delete(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || 'Error al eliminar template'
            );
        }
    }
);

// ==================== SLICE ====================

const vehicleTemplatesSlice = createSlice({
    name: 'vehicleTemplates',
    initialState,
    reducers: {
        // Seleccionar un template
        selectTemplate: (state, action: PayloadAction<VehicleTemplate | null>) => {
            state.selectedTemplate = action.payload;
        },

        // Limpiar template seleccionado
        clearSelectedTemplate: (state) => {
            state.selectedTemplate = null;
        },

        // Limpiar error
        clearTemplatesError: (state) => {
            state.error = null;
        },

        // Limpiar todo el estado
        resetTemplatesState: () => initialState,
    },
    extraReducers: (builder) => {
        // Fetch all templates
        builder
            .addCase(fetchVehicleTemplates.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchVehicleTemplates.fulfilled, (state, action) => {
                state.isLoading = false;
                state.templates = action.payload;
            })
            .addCase(fetchVehicleTemplates.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Fetch template by ID
        builder
            .addCase(fetchVehicleTemplateById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchVehicleTemplateById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedTemplate = action.payload;
            })
            .addCase(fetchVehicleTemplateById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Create template
        builder
            .addCase(createVehicleTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createVehicleTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                state.templates.push(action.payload);
            })
            .addCase(createVehicleTemplate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Update template
        builder
            .addCase(updateVehicleTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateVehicleTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                const updatedTemplate = action.payload;
                const index = state.templates.findIndex((t) => t.id === updatedTemplate.id);
                if (index !== -1) {
                    state.templates[index] = updatedTemplate;
                }
                if (state.selectedTemplate?.id === updatedTemplate.id) {
                    state.selectedTemplate = updatedTemplate;
                }
            })
            .addCase(updateVehicleTemplate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Delete template
        builder
            .addCase(deleteVehicleTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteVehicleTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                state.templates = state.templates.filter((t) => t.id !== action.payload);
                if (state.selectedTemplate?.id === action.payload) {
                    state.selectedTemplate = null;
                }
            })
            .addCase(deleteVehicleTemplate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

// Exportar acciones
export const {
    selectTemplate,
    clearSelectedTemplate,
    clearTemplatesError,
    resetTemplatesState,
} = vehicleTemplatesSlice.actions;

// Selectores
export const selectAllTemplates = (state: RootState) => state.vehicleTemplates.templates;
export const selectSelectedTemplate = (state: RootState) => state.vehicleTemplates.selectedTemplate;
export const selectTemplatesLoading = (state: RootState) => state.vehicleTemplates.isLoading;
export const selectTemplatesError = (state: RootState) => state.vehicleTemplates.error;

// Exportar reducer
export default vehicleTemplatesSlice.reducer;
